/**
 * financeiroKit — peças compartilhadas do módulo Financeiro (F2 / migration 017).
 *
 * Estilos (objeto S, inline no padrão do projeto — ver comercial/Catalogo.jsx),
 * metadados de status (rótulo + cor), o StatusBadge e a logo das contas
 * bancárias, reusados por Contas a pagar, Contas a receber, Saldo e Dashboard.
 * Status 'atrasada' já vem derivado do backend (conta 'prevista' vencida) —
 * aqui é só apresentação.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'

// data 'yyyy-mm-dd' (ou ISO) → 'dd/mm/aaaa'; vazio → '—'
export function fmtDate(v) {
  if (!v) return '—'
  const s = String(v).slice(0, 10)
  const [y, m, d] = s.split('-')
  return y && m && d ? `${d}/${m}/${y}` : s
}

// input <input type="date"> quer 'yyyy-mm-dd'; normaliza o valor vindo da API
export const toDateInput = (v) => (v ? String(v).slice(0, 10) : '')

// data de hoje em 'yyyy-mm-dd' (local) — usada ao marcar como pago/recebido
export function today() {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

// dias até o vencimento (negativo = vencida há N dias). null se sem data.
export function daysUntil(dateStr) {
  if (!dateStr) return null
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00')
  const h = new Date(); h.setHours(0, 0, 0, 0)
  return Math.round((d - h) / 86400000)
}

export const TIPOS_SALDO = ['banco', 'caixa', 'carteira_digital', 'investimento']
export const TIPO_SALDO_LABEL = {
  banco: 'Banco', caixa: 'Caixa',
  carteira_digital: 'Carteira digital', investimento: 'Investimento',
}

function c(fg, bg) { return { fg, bg } }

// ── Período ─────────────────────────────────────────────────────────────────
// Os presets fecham em datas de calendário: "Este mês" vai do dia 1 ao último
// dia do mês (não até hoje) e "Este ano" cobre jan–dez inteiros. É a mesma
// régua da dashboard; trocá-la aqui faz Contas e Saldo divergirem dos KPIs da
// visão geral para o mesmo período.
const PERIODOS = [
  { id: 'hoje', label: 'Hoje' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes', label: 'Este mês' },
  { id: 'ano', label: 'Este ano' },
]
export const PERIODO_PADRAO = 'ano'

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// 'yyyy-mm-dd' (ou ISO) → Date local à meia-noite
const dia = (v) => new Date(String(v).slice(0, 10) + 'T00:00:00')

export function periodoRange(id, custom) {
  const h = new Date(); h.setHours(0, 0, 0, 0)
  const y = h.getFullYear(), m = h.getMonth(), d = h.getDate()
  if (id === 'hoje') return { ini: new Date(y, m, d), fim: new Date(y, m, d) }
  // semana de segunda a domingo (padrão brasileiro; getDay() manda 0 no domingo)
  if (id === 'semana') {
    const seg = d - ((h.getDay() + 6) % 7)
    return { ini: new Date(y, m, seg), fim: new Date(y, m, seg + 6) }
  }
  if (id === 'mes') return { ini: new Date(y, m, 1), fim: new Date(y, m + 1, 0) }
  if (id === 'ano') return { ini: new Date(y, 0, 1), fim: new Date(y, 11, 31) }
  if (id === 'custom' && custom?.ini && custom?.fim) return { ini: dia(custom.ini), fim: dia(custom.fim) }
  return periodoRange(PERIODO_PADRAO)
}

// Janela imediatamente anterior, do mesmo tamanho — base da variação dos cards.
export function rangeAnterior(range) {
  const dias = Math.round((range.fim - range.ini) / 86400000) + 1
  const fim = new Date(range.ini); fim.setDate(fim.getDate() - 1)
  const ini = new Date(fim); ini.setDate(ini.getDate() - dias + 1)
  return { ini, fim }
}

export const inRange = (v, range) => {
  if (!range) return true
  if (!v) return false
  const d = dia(v)
  return d >= range.ini && d <= range.fim
}

/** Fecha o popover ao clicar fora / apertar Esc. */
export function useFechaFora(aberto, fechar) {
  const ref = useRef(null)
  useEffect(() => {
    if (!aberto) return undefined
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) fechar() }
    const esc = (e) => { if (e.key === 'Escape') fechar() }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc) }
  }, [aberto, fechar])
  return ref
}

/**
 * Seletor de período da barra de filtros (classes `fc-*`, de contas.css).
 * As datas do intervalo livre ficam escondidas até pedirem, e só são aplicadas
 * com as duas preenchidas e na ordem certa.
 */
export function PeriodoFiltro({ periodo, custom, onChange }) {
  const [open, setOpen] = useState(false)
  const [mostrarDatas, setMostrarDatas] = useState(periodo === 'custom')
  const [rascunho, setRascunho] = useState(custom)
  const ref = useFechaFora(open, useCallback(() => setOpen(false), []))

  const rotulo = periodo === 'custom'
    ? `${fmtDate(custom.ini)} – ${fmtDate(custom.fim)}`
    : (PERIODOS.find((p) => p.id === periodo)
       || PERIODOS.find((p) => p.id === PERIODO_PADRAO)).label
  const valido = rascunho.ini && rascunho.fim && rascunho.ini <= rascunho.fim

  return (
    <div className="fc-pop" ref={ref}>
      <button type="button" className="fc-ctrl" onClick={() => setOpen((o) => !o)}>
        <Icon n="calendar" s={15} c="#667085" /> {rotulo}
        <span className="fc-ctrl-caret"><Icon n="chevron_d" s={13} /></span>
      </button>
      {open && (
        <div className="fc-pop-menu">
          {PERIODOS.map((p) => (
            <button key={p.id} type="button"
              className={`fc-pop-item${periodo === p.id ? ' active' : ''}`}
              onClick={() => { setMostrarDatas(false); onChange(p.id); setOpen(false) }}>
              {p.label}
            </button>
          ))}

          <div className="fc-pop-sep" />
          <button type="button"
            className={`fc-pop-item${periodo === 'custom' ? ' active' : ''}`}
            onClick={() => setMostrarDatas((v) => !v)}>
            Selecionar período
          </button>

          {mostrarDatas && (
            <div className="fc-pop-custom">
              <label>
                De
                <input type="date" value={rascunho.ini} max={rascunho.fim || undefined}
                  onChange={(e) => setRascunho((r) => ({ ...r, ini: e.target.value }))} />
              </label>
              <label>
                Até
                <input type="date" value={rascunho.fim} min={rascunho.ini || undefined}
                  onChange={(e) => setRascunho((r) => ({ ...r, fim: e.target.value }))} />
              </label>
              <button type="button" className="fc-ctrl primary" disabled={!valido}
                onClick={() => { onChange('custom', rascunho); setOpen(false) }}>
                Aplicar período
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Páginas exibidas na paginação: 1..5 … última (padrão da referência). */
export function listaPaginas(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (page <= 4) return [1, 2, 3, 4, 5, '…', total]
  if (page >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '…', page - 1, page, page + 1, '…', total]
}

// ── Logo das contas financeiras ─────────────────────────────────────────────
// Logos disponíveis em public/logos/bancos/. Registro explícito (e não
// `${slug}.svg`) porque os arquivos vêm da página de marca de cada empresa e
// cada uma entrega num formato: svg, png, webp, avif.
const LOGOS_BANCOS = {
  asaas: 'asaas.png',
  'banco-do-brasil': 'banco-do-brasil.png',
  bradesco: 'bradesco.png',
  'c6-bank': 'c6-bank.png',
  caixa: 'caixa.png',
  inter: 'inter.webp',
  itau: 'itau.avif',
  'mercado-pago': 'mercado-pago.png',
  nubank: 'nubank.png',
  pagbank: 'pagbank.png',
  paypal: 'paypal.svg',
  santander: 'santander.svg',
  sicoob: 'sicoob.png',
  sicredi: 'sicredi.png',
  stone: 'stone.png',
}

// O nome da conta é digitado livremente ("Conta Corrente Itaú", "BB", "Caixa
// Econômica Federal"…), então variações conhecidas caem no mesmo slug.
const ALIAS_BANCOS = {
  bb: 'banco-do-brasil',
  'banco-brasil': 'banco-do-brasil',
  'caixa-economica': 'caixa',
  'caixa-economica-federal': 'caixa',
  cef: 'caixa',
  'banco-inter': 'inter',
  'itau-unibanco': 'itau',
  'banco-itau': 'itau',
  mercadopago: 'mercado-pago',
  nu: 'nubank',
  'nu-pagamentos': 'nubank',
  'banco-bradesco': 'bradesco',
  'banco-santander': 'santander',
  'pay-pal': 'paypal',
  c6: 'c6-bank',
  'banco-c6': 'c6-bank',
  pagseguro: 'pagbank',
  'pag-seguro': 'pagbank',
  'pagseguro-pagbank': 'pagbank',
  'banco-sicoob': 'sicoob',
  'banco-sicredi': 'sicredi',
  'stone-pagamentos': 'stone',
  ton: 'stone',
}

const slugBanco = (nome) => String(nome || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

// Arquivo da logo para o nome da conta; null quando não há logo cadastrada.
// Como o nome costuma vir composto ("Conta Corrente Itaú"), cada palavra também
// é testada isoladamente antes de desistir.
function logoBanco(nome) {
  const slug = slugBanco(nome)
  if (!slug) return null
  const achar = (s) => (LOGOS_BANCOS[s] ? s : ALIAS_BANCOS[s])
  const key = achar(slug) || slug.split('-').map(achar).find(Boolean)
  return key && LOGOS_BANCOS[key] ? `/logos/bancos/${LOGOS_BANCOS[key]}` : null
}

/**
 * Quadrado com a logo do banco (classe `fd-account-logo`, de
 * financeiro-dashboard.css). Sem logo cadastrada — ou com o arquivo quebrado —
 * cai num monograma colorido, para nunca sobrar um quadrado vazio.
 */
export function AccountLogo({ nome, size }) {
  const [falhou, setFalhou] = useState(false)
  const src = logoBanco(nome)
  const mono = String(nome || '?').replace(/[^A-Za-zÀ-ÿ0-9]/g, '').slice(0, 2).toUpperCase()
  const estilo = {
    background: src && !falhou ? '#fff' : '#1e293b',
    ...(size ? { width: size, height: size } : null),
  }
  return (
    <span className="fd-account-logo" style={estilo}>
      {src && !falhou
        ? <img src={src} alt={nome} onError={() => setFalhou(true)} />
        : mono}
    </span>
  )
}

function StatusBadge({ status, map }) {
  const meta = map[status] || { label: status, fg: '#475569', bg: '#e2e8f0' }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 100,
      fontSize: 11.5, fontWeight: 700, color: meta.fg, background: meta.bg,
    }}>
      {meta.label}
    </span>
  )
}

const S = {
  // barra de filtros / topo
  bar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  barRight: { display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexWrap: 'wrap' },
  searchBox: { minWidth: 200, maxWidth: 320 },
  filterSelect: {
    padding: '8px 10px', borderRadius: 8, border: '1px solid var(--soft)',
    background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  // lista (row-cards) — mesmo visual do Catálogo
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  rowCard: {
    display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
    padding: '12px 16px', borderRadius: 12, border: '1px solid var(--soft)', background: 'var(--surface)',
  },
  colName: { flex: '1 1 220px', minWidth: 0 },
  name: { fontSize: 14.5, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: 12, color: 'var(--sec)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colMeta: { display: 'flex', flexDirection: 'column', gap: 2, flex: '0 0 auto', minWidth: 96 },
  metaLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)' },
  metaValue: { fontSize: 13, color: 'var(--sec)', fontWeight: 500 },
  metaValor: { fontSize: 14, color: 'var(--text)', fontWeight: 700 },
  actions: { display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 },
  iconBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
    borderRadius: 8, border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--sec)', cursor: 'pointer',
  },

  muted: { color: 'var(--sec)', fontSize: 14 },
  error: {
    padding: '10px 14px', borderRadius: 8, background: '#fdecea',
    border: '1px solid #f5c6cb', color: '#c0392b', marginBottom: 12, fontSize: 13,
  },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--text)', background: 'var(--text)', color: 'var(--surface)',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  btnGhost: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--soft)',
    background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  // modal
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px', overflowY: 'auto',
  },
  modal: {
    width: 480, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--soft)',
    borderRadius: 14, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,.25)',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' },
  x: { border: 'none', background: 'transparent', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: 'var(--sec)' },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--sec)', margin: '10px 0 4px' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14,
  },
  row: { display: 'flex', gap: 10 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },

  // dashboard
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 },
  card: { padding: '16px 18px', borderRadius: 14, border: '1px solid var(--soft)', background: 'var(--surface)' },
  cardLabel: { fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)' },
  cardValue: { fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 6 },
  chartBox: { padding: '16px 18px', borderRadius: 14, border: '1px solid var(--soft)', background: 'var(--surface)' },
  chartTitle: { fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },

  // ── Bloco 1: barra de filtros (controles de mesma altura) ───────────────────
  filterBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  ctrl: {
    padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: 12,
    background: 'var(--bg)', color: 'var(--text)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', outline: 'none', height: 43, boxSizing: 'border-box',
  },
  searchGrow: { flex: '1 1 220px', minWidth: 180, maxWidth: 360 },
  btnFilters: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 14px', height: 43, boxSizing: 'border-box',
    border: '1.5px solid var(--border)', borderRadius: 12, background: 'var(--bg)',
    color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  popWrap: { position: 'relative' },
  popOverlay: { position: 'fixed', inset: 0, zIndex: 40 },
  popPanel: {
    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41, width: 250,
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
    boxShadow: '0 12px 32px rgba(0,0,0,.16)', padding: 14,
  },
  popTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)', marginBottom: 8 },
  popRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer', padding: '4px 0' },
  popLink: { border: 'none', background: 'transparent', color: 'var(--sec)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginTop: 10 },
  popItem: {
    display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent',
    padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer',
  },
  popItemActive: { background: 'var(--bg)', color: 'var(--text)' },
  popField: { fontSize: 12, fontWeight: 700, color: 'var(--sec)', margin: '10px 0 4px', display: 'block' },
  dateRow: { display: 'flex', gap: 8, marginTop: 6 },
  dateInput: {
    flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--soft)',
    background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box',
  },
  divider: { height: 1, background: 'var(--soft)', margin: '10px 0' },

  // seleção em massa (bulk bar)
  bulkBar: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 10,
    borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', flexWrap: 'wrap',
  },
  bulkBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--text)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  chkCell: { width: 36, textAlign: 'center' },

  // ── Bloco 2: cards de resumo compactos e clicáveis ──────────────────────────
  cardsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: 10, marginBottom: 14 },
  cardBtn: {
    display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start',
    padding: '10px 14px', borderRadius: 12, border: '1.5px solid var(--border)',
    background: 'var(--surface)', cursor: 'pointer', textAlign: 'left',
    transition: 'border-color .12s, box-shadow .12s',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 6 },
  cardDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  cardLbl: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--ter)' },
  cardVal: { fontSize: 18, fontWeight: 800, lineHeight: 1.15, color: 'var(--text)' },

  // ── Listagem no padrão da página de Leads (container definido) ───────────────
  listWrap: { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' },
  ltable: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  lth: {
    textAlign: 'left', padding: '11px 14px', fontSize: 11.5, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--sec)',
    background: 'var(--bg)', borderBottom: '1px solid var(--soft)', whiteSpace: 'nowrap',
  },
  ltr: { borderBottom: '1px solid var(--soft)', transition: 'background .12s' },
  ltrHover: { background: 'var(--bg)' },
  ltrOverdue: { background: 'rgba(220,38,38,.05)' },
  ltd: { padding: '12px 14px', color: 'var(--sec)', verticalAlign: 'middle' },
  lprimary: { fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 },
  lsecondary: { fontSize: 12, color: 'var(--ter)', marginTop: 2 },
  recorrente: {
    display: 'inline-block', marginTop: 3, fontSize: 10.5, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)',
    background: 'var(--bg)', border: '1px solid var(--soft)', borderRadius: 100, padding: '1px 7px',
  },
  dueTag: { fontSize: 11, fontWeight: 700, marginTop: 2 },
  rowActions: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' },
  iconOk: { color: '#166534', borderColor: '#bbf7d0' },

  // paginação
  pag: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 12, flexWrap: 'wrap', gap: 8,
  },
  pagBtns: { display: 'flex', alignItems: 'center', gap: 6 },
  pagBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
    borderRadius: 8, border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--sec)', cursor: 'pointer',
  },
  pagInfo: { fontSize: 13, fontWeight: 600, color: 'var(--sec)', minWidth: 54, textAlign: 'center' },
}
